// src/pages/doctor/ClinicalInsightPanelV1.jsx
import React from 'react';

/**
 * ローディング状態
 */
const LoadingState = ({ message = 'AIサマリを生成中です...' }) => (
  <div className="mcai-loading-state">
    <div className="mcai-loading-spinner"></div>
    <span>{message}</span>
  </div>
);

/**
 * エラー状態
 */
const ErrorState = ({ message }) => (
  <div className="mcai-error-state">
    <span className="mcai-error-icon">⚠️</span>
    <span>生成に失敗しました{message && `（${message}）`}</span>
  </div>
);

/**
 * 空状態
 */
const EmptyState = ({ message = '診察開始時に自動生成されます。' }) => (
  <div className="mcai-empty-state">
    <span className="mcai-empty-icon">📝</span>
    <span>{message}</span>
  </div>
);

/**
 * テキストコンテンツセクション（装飾なしのプレーンテキスト用）
 */
const TextContentSection = ({ icon, title, data, headerStyle }) => {
  const isCompleted = data.status === 'completed' && data.content;
  const isProcessing = data.status === 'processing';
  const isFailed = data.status === 'failed';

  return (
    <div className="mcai-main-section">
      <div className="mcai-main-section-header" style={headerStyle}>
        <span className="mcai-main-section-icon">{icon}</span>
        <span className="mcai-main-section-title">{title}</span>
        {isProcessing && (
          <span className="mcai-badge mcai-badge-processing">生成中...</span>
        )}
      </div>
      
      <div className="mcai-main-section-content">
        {isCompleted ? (
          <>
            <div 
              className="mcai-text-content" 
              style={{ 
                lineHeight: '1.8', 
                color: '#1e293b', 
                whiteSpace: 'pre-wrap',
                padding: '12px 0'
              }}
            >
              {data.content}
            </div>
            {data.generated_at && (
              <div className="mcai-timestamp">
                生成日時: {new Date(data.generated_at).toLocaleString('ja-JP')}
              </div>
            )}
          </>
        ) : isProcessing ? (
          <LoadingState />
        ) : isFailed ? (
          <ErrorState message={data.error_message} />
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
};

/**
 * メインコンポーネント - 新しい3セクション構成
 */
const ClinicalInsightPanelV1 = ({ clinicalInsights = {} }) => {
  const patientInfo = clinicalInsights?.patient_info || {};
  const treatmentHistory = clinicalInsights?.treatment_history || {};
  const treatmentProposal = clinicalInsights?.treatment_proposal || {};
  
  // 治療履歴があるかどうか（過去の診察がある場合のみ表示）
  const hasTreatmentHistoryData = treatmentHistory.status && treatmentHistory.status !== 'not_found';

  return (
    <div className="medichat-patient-section">
      <details className="medichat-patient-details">
        <summary>
          🧠 AIサマリ
        </summary>

        <div className="mcai-panel-container">
          {/* 免責事項 */}
          <div className="mcai-disclaimer">
            <span className="mcai-disclaimer-icon">ℹ️</span>
            <span>
              このAIサマリは<strong>医師の意思決定を補助</strong>する情報です。確定診断ではありません。
            </span>
          </div>

          {/* 1. 患者情報 */}
          <TextContentSection 
            icon="👤"
            title="患者情報"
            data={patientInfo}
            headerStyle={{
              background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)'
            }}
          />

          {/* 2. 治療履歴（過去の診察がある場合のみ） */}
          {hasTreatmentHistoryData && (
            <TextContentSection 
              icon="📋"
              title="治療履歴"
              data={treatmentHistory}
              headerStyle={{
                background: 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)'
              }}
            />
          )}

          {/* 3. 治療提案 */}
          <TextContentSection 
            icon="💡"
            title="治療提案"
            data={treatmentProposal}
            headerStyle={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)'
            }}
          />
        </div>
      </details>
    </div>
  );
};

export default ClinicalInsightPanelV1;
